import { InputNumber, InputNumberProps } from 'antd';
import React, { Ref, useEffect, useRef } from 'react';
import mergeRefs from '../MergeRefs';

export interface Props extends InputNumberProps<number> {
    ref?: Ref<HTMLInputElement>,
    /** Allow changing value using mouse wheel. Default true. */
    allowWheelUpdate?: boolean,
}

/**
 * AntD InputNumber with added functionality.
 * - allowWheelUpdate: Allows changing value using mouse wheel.
 */
const InputNumberExt = React.forwardRef<HTMLInputElement, Omit<Props, "enterKeyHint">>(({
    allowWheelUpdate = true,
    onFocus = () => { },
    onBlur = () => { },
    ...rest
}, ref) => { 

    const wheelEventElement = useRef<HTMLInputElement>();
    const internalref = useRef<HTMLInputElement>(null);
    const restRef = useRef<Props>();

    const focussed = useRef<boolean>();

    useEffect(() => {
        return () => removeWheelListener();
    }, [])

    useEffect(() => {
        restRef.current = rest;
    }, [rest])

    const mergedRef = mergeRefs([internalref, ref], element => {       
        if (element && element !== wheelEventElement.current) {
            removeWheelListener()
            wheelEventElement.current = element;
            element.addEventListener("wheel", handleWheel, { passive: false })
        }
    }); 

    const removeWheelListener = () => wheelEventElement.current?.removeEventListener("wheel", handleWheel);

    const handleWheel = (evt: WheelEvent) => {  
        if (!restRef.current || restRef.current.readOnly || restRef.current.disabled || !restRef.current.onChange || !allowWheelUpdate || !focussed.current)
            return;

        const { step, precision, min, max, onChange } = restRef.current;

        let stepF = parseFloat(`${step ?? 1}`);
        if (isNaN(stepF))
            stepF = 1;

        const increment = stepF * (-evt.deltaY / Math.abs(evt.deltaY));
        if (increment) {
            const current = Number((evt.currentTarget as HTMLInputElement)?.value);
            if (!isNaN(current)) {
                var value = current + increment;
                if ((min === undefined || value >= min) && (max === undefined || value <= max)) {
                    if (precision !== undefined)
                        value = Number(value.toFixed(precision));                    
                  
                    onChange(value);                    
                }
            }
            evt.stopPropagation();
            evt.preventDefault();
        }       
    };

    return <InputNumber<number>
        ref={mergedRef}
        onFocus={evt => {
            focussed.current = true;
            onFocus(evt);
        }}
        onBlur={evt => {
            focussed.current = false
            onBlur(evt);
        }}
        {...rest}
    />
})

export default InputNumberExt;